import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { message } from "antd";

// UserNotification interface based on your API response
export interface UserNotification {
  id: string;
  data: {
    message: string;
  };
  read_at: string | null;
  created_at: string;
  updated_at: string;
}

// Hook to fetch notifications (called once on mount, then updated via Socket.io)
export const useNotifications = () => {
  return useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const response = await apiClient.get<UserNotification[]>("/notification");
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: true, // Enable refetch on window focus for better UX
    refetchInterval: false, // No automatic polling since we use Socket.io
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

// Hook to get unread notifications count (derived from notifications data)
export const useUnreadNotificationsCount = () => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["unreadNotificationsCount"],
    queryFn: () => {
      // Always get from cache first to avoid unnecessary API calls
      const cachedNotifications = queryClient.getQueryData<UserNotification[]>([
        "notifications",
      ]);

      if (cachedNotifications) {
        return cachedNotifications.filter(
          (notification) => !notification.read_at
        ).length;
      }

      // If no cached data, return 0 and let the notifications query handle the API call
      return 0;
    },
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    // This query depends on notifications data
    enabled: !!queryClient.getQueryData(["notifications"]),
  });
};

// Hook to get read notifications
export const useReadNotifications = () => {
  return useQuery({
    queryKey: ["readNotifications"],
    queryFn: async () => {
      const response = await apiClient.get<UserNotification[]>(
        "/notification/read-notifications"
      );
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });
};

// Hook to mark notification as read (keeping original endpoint)
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await apiClient.put(`/notification/${notificationId}`);
      return response.data;
    },
    onSuccess: (data, notificationId) => {
      // Update the notification in cache
      queryClient.setQueryData<UserNotification[]>(["notifications"], (old) => {
        if (!old) return old;
        return old.map((notification) =>
          notification.id === notificationId
            ? { ...notification, read_at: new Date().toISOString() }
            : notification
        );
      });

      // Invalidate unread count to trigger recalculation
      queryClient.invalidateQueries({ queryKey: ["unreadNotificationsCount"] });

      // Also invalidate read notifications cache
      queryClient.invalidateQueries({ queryKey: ["readNotifications"] });

      message.success("Notification marked as read");
    },
    onError: (error: any) => {
      console.error("Failed to mark notification as read:", error);
      message.error("Failed to mark notification as read");
    },
  });
};

// Hook to mark all notifications as read
export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Get unread notifications from cache
      const cachedNotifications =
        queryClient.getQueryData<UserNotification[]>(["notifications"]) || [];
      const unreadNotifications = cachedNotifications.filter(
        (notification) => !notification.read_at
      );

      if (unreadNotifications.length === 0) {
        return { count: 0 };
      }

      // Mark each unread notification as read using individual PUT requests
      const markAsReadPromises = unreadNotifications.map((notification) =>
        apiClient.put(`/notification/${notification.id}`)
      );

      await Promise.all(markAsReadPromises);
      return { count: unreadNotifications.length };
    },
    onSuccess: (data) => {
      // Update all notifications in cache
      queryClient.setQueryData<UserNotification[]>(["notifications"], (old) => {
        if (!old) return old;
        return old.map((notification) => ({
          ...notification,
          read_at: notification.read_at || new Date().toISOString(),
        }));
      });

      // Invalidate unread count
      queryClient.invalidateQueries({ queryKey: ["unreadNotificationsCount"] });

      // Also invalidate read notifications cache
      queryClient.invalidateQueries({ queryKey: ["readNotifications"] });

      if (data.count > 0) {
        message.success(`Marked ${data.count} notifications as read`);
      } else {
        message.info("No unread notifications to mark");
      }
    },
    onError: (error: any) => {
      console.error("Failed to mark all notifications as read:", error);
      message.error("Failed to mark all notifications as read");
    },
  });
};

// Hook to manually refresh notifications
export const useRefreshNotifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.get<UserNotification[]>("/notification");
      return response.data;
    },
    onSuccess: (data) => {
      // Update both queries
      queryClient.setQueryData(["notifications"], data);
      queryClient.invalidateQueries({ queryKey: ["unreadNotificationsCount"] });
      message.success("Notifications refreshed");
    },
    onError: (error: any) => {
      console.error("Failed to refresh notifications:", error);
      message.error("Failed to refresh notifications");
    },
  });
};

// Hook to add new notification to cache (used by socket service)
export const useAddNotificationToCache = () => {
  const queryClient = useQueryClient();

  return (newNotification: UserNotification) => {
    // Add new notification to the beginning of the list
    queryClient.setQueryData<UserNotification[]>(["notifications"], (old) => {
      if (!old) return [newNotification];
      return [newNotification, ...old];
    });

    // Invalidate unread count to trigger recalculation
    queryClient.invalidateQueries({ queryKey: ["unreadNotificationsCount"] });
  };
};
